import React, { useCallback, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Button, Pane, PlusIcon, SelectField, Text, Tooltip, InfoSignIcon } from 'evergreen-ui';

import styles from './Select.module.scss';

const Select = ({
  className, defaultValue, value, onChange, options, label, disabled, inline, error, required, onAddItem,
  addItemLabel, tooltip,
}) => {
  const selectRef = useRef(null);

  const onValueChange = useCallback((event) => {
    onChange(event.target.value);
  }, [onChange]);

  return (
    <div className={cn(
      styles.selectContainer,
      disabled && styles.disabledSelectContainer,
      inline && styles.inline,
      className,
    )}
    >
      <SelectField
        ref={selectRef}
        onChange={onValueChange}
        {...(defaultValue != null && { defaultValue: defaultValue })}
        {...(value != null && { value: value })}
        className={cn(styles.select, {
          [styles.disabled]: disabled,
          [styles.error]: !!error?.message,
          [styles.placeholder]: !value && !defaultValue,
        })}
        label={
          label ? (
            <Pane className={styles.labelContainer}>
              <Text
                fontWeight={600}
                className={cn(styles.label, {
                  [styles.disabled]: disabled,
                  [styles.error]: !!error?.message,
                })}
              >
                {label}
                {required && '*'}
                {tooltip &&
                  <Tooltip content={tooltip}>
                    <InfoSignIcon color="disabled" />
                  </Tooltip>
                }
              </Text>
              {
                onAddItem && (
                  <Button
                    appearance="minimal"
                    onClick={(e) => {
                      e.preventDefault();
                      onAddItem(e);
                    }}
                    className={styles.addItem}
                    iconBefore={(<PlusIcon size={14} />)}
                    height={24}
                  >
                    <Text>{addItemLabel || 'Add'}</Text>
                  </Button>
                )
              }
            </Pane>
          ) : (
            <></>
          )
        }
        disabled={disabled}
        marginBottom={0}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </SelectField>
      {
        error && <Text size={300} className={styles.errorText}>{error.message}</Text>
      }
    </div>
  );
};

Select.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  defaultValue: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  required: PropTypes.bool,
  onAddItem: PropTypes.func,
  addItemLabel: PropTypes.string,
  tooltip: PropTypes.string,
};

Select.defaultProps = {
  label: null,
  value: null,
  className: null,
  defaultValue: null,
  disabled: false,
  inline: false,
  error: null,
  required: false,
  onAddItem: null,
  addItemLabel: null,
  tooltip: null,
};

export default React.memo(Select);